import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import { Link, useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { useFacets, useGenres, useItems, useSearchItems } from '@/api/queries';
import { ItemFilters, ItemState, ValidationLevel } from '@/api/graphql';
import StateChip from '@/components/StateChip';
import AssetThumbnail from '@/components/AssetThumbnail';
import ValidationChip from '@/components/ValidationChip';
import RefreshButton from '@/components/RefreshButton';
import { formatDateTime, humaniseState } from '@/utils';

const PAGE_SIZE = 100;

/**
 * One row shape for both modes. Browsing reads DynamoDB and search reads the index, and the two return
 * different documents — flattening here keeps the grid from having to know which it is rendering.
 */
interface Row {
  assetId: string;
  version: string;
  provider: string;
  state: ItemState;
  title: string;
  showType: string;
  worstValidationLevel?: ValidationLevel | null;
  /**
   * Cover art. Search gets this straight from the index; browsing derives it from the item's assets,
   * which is why the two modes pick it differently below rather than sharing one expression.
   */
  posterUrl?: string | null;
}

/**
 * The one image that represents an item in the grid.
 *
 * Same preference order as the search index uses (`search/document.ts`), so a title does not change
 * its thumbnail depending on whether you got to it by browsing or by searching. Only assets with a
 * resolved URL qualify — a declared-but-undelivered asset has none.
 */
const posterOf = (
  assets: { type?: string | null; url?: string | null }[] | undefined,
): string | null | undefined => {
  // Defensive because the caller is a GraphQL selection, not a type guarantee: a query that omits
  // `assets` yields undefined at runtime while still type-checking, and a crash in a list renderer
  // takes the whole catalogue down rather than one cell.
  const delivered = (assets ?? []).filter((asset) => asset.url);
  return (
    delivered.find((asset) => asset.type === 'poster')?.url ??
    delivered.find((asset) => asset.type === 'box cover')?.url ??
    delivered[0]?.url
  );
};

/** Effective value of a wrapped field: the operator's override if set, else what the provider sent. */
const effective = (field?: {
  original?: string | null;
  editorial?: string | null;
}) => field?.editorial ?? field?.original ?? '';

/**
 * One filter dropdown, driven by what the catalogue actually contains.
 *
 * Multi-select with counts, because both were things the single hard-coded dropdown could not do:
 * "show me Movies and Specials" was impossible, and there was no way to tell whether a value would
 * return anything before selecting it.
 *
 * A value with a count of zero cannot occur — the facets come from the index, so every option listed
 * has at least one item behind it.
 */
/** The detail route for a row. One definition, so the link and the row click cannot drift apart. */
const itemPath = (row: { assetId: string; version: string }) =>
  `/catalogue/${encodeURIComponent(row.assetId)}/${encodeURIComponent(
    row.version,
  )}`;

const FacetSelect = ({
  label,
  selected,
  onChange,
  options,
  minWidth = 170,
  labelFor,
}: {
  label: string;
  selected: string[];
  onChange: (values: string[]) => void;
  options?: { value: string; count: number }[];
  minWidth?: number;
  /** Renders a facet value for display. The value sent as a filter is always the indexed one. */
  labelFor?: (value: string) => string;
}) => (
  <TextField
    select
    label={label}
    size="small"
    sx={{ minWidth }}
    value={selected}
    disabled={!options?.length}
    SelectProps={{
      multiple: true,
      // Without this the field renders "[object Object]" for a multi-select; it also keeps the
      // closed state compact when several values are chosen.
      renderValue: (value) =>
        (value as string[]).map((v) => labelFor?.(v) ?? v).join(', '),
    }}
    onChange={(e) => {
      const value = e.target.value;
      onChange(
        typeof value === 'string'
          ? value.split(',')
          : (value as unknown as string[]),
      );
    }}
  >
    {(options ?? []).map((option) => (
      <MenuItem key={option.value} value={option.value}>
        <Checkbox size="small" checked={selected.includes(option.value)} />
        <ListItemText primary={labelFor?.(option.value) ?? option.value} />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {option.count}
        </Typography>
      </MenuItem>
    ))}
  </TextField>
);

const Catalogue = () => {
  const navigate = useNavigate();
  const [provider, setProvider] = useState<string[]>([]);
  const [state, setState] = useState<string[]>([]);
  const [level, setLevel] = useState<string[]>([]);
  const [showType, setShowType] = useState<string[]>([]);
  const [genre, setGenre] = useState<string[]>([]);
  const [language, setLanguage] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  // Debounced so a query is not issued on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useDebounce(() => setDebouncedSearch(search), 300, [search]);

  // What the catalogue actually contains, with counts — not the configured vocabularies, which are a
  // different question and would offer values no item carries.
  const { data: facets } = useFacets();
  // Only to label the genre facet — the value sent as a filter is always the indexed one.
  const { data: genres } = useGenres();

  const filters = useMemo<ItemFilters | undefined>(() => {
    const f: ItemFilters = {};
    // An empty selection is no filter. Sending `[]` would match nothing and read as an empty
    // catalogue rather than an unfiltered one.
    if (provider.length) f.provider = provider;
    if (state.length) f.state = state as ItemState[];
    if (level.length) f.validationLevel = level as ValidationLevel[];
    if (showType.length) f.showType = showType;
    if (genre.length) f.genre = genre;
    if (language.length) f.language = language;
    return Object.keys(f).length ? f : undefined;
  }, [provider, state, level, showType, genre, language]);

  const activeFilterCount =
    provider.length +
    state.length +
    level.length +
    showType.length +
    genre.length +
    language.length;

  const clearFilters = () => {
    setProvider([]);
    setState([]);
    setLevel([]);
    setShowType([]);
    setGenre([]);
    setLanguage([]);
  };

  const isSearching = debouncedSearch.trim().length > 0;

  // Both hooks always run as hooks; `enabled` inside them decides which actually fetches. Hooks cannot
  // be called conditionally.
  const browse = useItems(PAGE_SIZE, filters);
  const searchResults = useSearchItems(debouncedSearch, PAGE_SIZE, filters);
  const active = isSearching ? searchResults : browse;

  const rows = useMemo<Row[]>(() => {
    if (isSearching) {
      return (searchResults.data?.items ?? []).map((item) => ({
        assetId: item.assetId,
        version: item.version,
        provider: item.provider,
        state: item.state,
        title: item.title ?? '',
        showType: item.showType ?? '',
        worstValidationLevel: item.worstValidationLevel,
        posterUrl: item.posterUrl,
      }));
    }
    return (browse.data?.items ?? []).map((item) => ({
      assetId: item.assetId,
      version: item.version,
      provider: item.provider,
      state: item.state,
      title: effective(item.properties.title),
      showType: effective(item.properties.showType),
      worstValidationLevel: item.worstValidationLevel,
      posterUrl: posterOf(item.properties.assets),
    }));
  }, [isSearching, searchResults.data, browse.data]);

  const columns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: 'posterUrl',
        headerName: '',
        width: 72,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <AssetThumbnail url={row.posterUrl} alt={row.title} size={40} />
        ),
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 2,
        minWidth: 220,
        // A real anchor, not just a clickable row. `onRowClick` navigates programmatically, which the
        // browser cannot interpret: ctrl/cmd-click opened the item in the same tab instead of a new one,
        // middle-click did nothing, and "Open link in new tab" was not in the context menu at all. A
        // Link gives all three back for free, and keyboard users get a focusable target.
        renderCell: ({ row }: GridRenderCellParams<Row>) => (
          <Link
            to={itemPath(row)}
            // The row handler would otherwise fire too and navigate in this tab as well.
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {row.title}
          </Link>
        ),
      },
      { field: 'assetId', headerName: 'Asset ID', flex: 1, minWidth: 180 },
      { field: 'provider', headerName: 'Provider', width: 130 },
      { field: 'showType', headerName: 'Type', width: 120 },
      {
        field: 'state',
        headerName: 'State',
        width: 170,
        renderCell: ({ row }) => <StateChip state={row.state} />,
        sortable: false,
      },
      {
        field: 'worstValidationLevel',
        headerName: 'Validation',
        width: 140,
        renderCell: ({ row }) => (
          <ValidationChip level={row.worstValidationLevel} />
        ),
        sortable: false,
      },
      {
        field: 'version',
        headerName: 'Ingested',
        width: 190,
        valueFormatter: (params) => formatDateTime(params.value as string),
      },
    ],
    [],
  );

  return (
    <>
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h5">Catalogue</Typography>
        {/* Say which engine answered, so a surprising result set is diagnosable rather than mysterious. */}
        <Chip
          size="small"
          variant="outlined"
          color={isSearching ? 'primary' : 'default'}
          label={
            isSearching
              ? `search · ${searchResults.data?.total ?? 0} match${
                  searchResults.data?.total === 1 ? '' : 'es'
                } across the whole catalogue`
              : `browsing · newest ${PAGE_SIZE}`
          }
        />
        <Box sx={{ flexGrow: 1 }} />
        {/* The catalogue is a shared view of a pipeline: packages arrive by themselves, and colleagues
            edit and publish while it is open. Without this the only way to see any of that was a
            browser reload, which throws away the filters and the search along with the stale data. */}
        <RefreshButton
          queryKeys={['items', 'searchItems', 'facets']}
          busy={active.isFetching}
        />
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" gap={2} flexWrap="wrap">
          <TextField
            label="Search"
            placeholder="Title, cast, summary, asset ID…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <FacetSelect
            label="Provider"
            selected={provider}
            onChange={setProvider}
            options={facets?.provider}
            minWidth={200}
          />
          <FacetSelect
            label="Type"
            selected={showType}
            onChange={setShowType}
            options={facets?.showType}
          />
          <FacetSelect
            label="Genre"
            selected={genre}
            onChange={setGenre}
            options={facets?.genre}
            // The facet is lowercased so the same genre cannot appear twice; show the configured
            // spelling. Falls back to the raw value for a genre with no vocabulary entry, which stays
            // visible rather than being hidden behind a blank.
            labelFor={(value) =>
              genres?.find((g) => g.id.toLowerCase() === value)?.name ?? value
            }
          />
          {/* Driven by the index like every other facet, so it lists the languages actually present
              rather than a hard-coded set — the mistake the provider dropdown made originally. */}
          <FacetSelect
            label="Language"
            selected={language}
            onChange={setLanguage}
            options={facets?.language}
          />
          {/* Both of these listed the raw enum — EXPORT_COMPLETED, WARNING — beside a table whose
              chips read "Export completed" and "warnings". The value sent to the API is still the
              enum; only the label changes, so the filter names states the way the rest of the screen
              already does. */}
          <FacetSelect
            label="State"
            selected={state}
            onChange={setState}
            options={facets?.state}
            labelFor={humaniseState}
            minWidth={190}
          />
          <FacetSelect
            label="Validation"
            selected={level}
            onChange={setLevel}
            options={facets?.validationLevel}
            labelFor={humaniseState}
          />
          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
            >
              Clear {activeFilterCount}
            </Button>
          )}
        </Stack>
      </Paper>

      {active.error && (
        <Alert severity="error">
          {active.error instanceof Error
            ? active.error.message
            : 'Failed to load items'}
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, minHeight: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={active.isLoading || active.isFetching}
          getRowId={(row: Row) => `${row.assetId}#${row.version}`}
          onRowClick={({ row }: GridRowParams<Row>, event) => {
            // Clicking anywhere on the row is a convenience, so it has to honour the modifiers a link
            // would: ctrl/cmd or shift means "somewhere else", and hijacking that is worse than not
            // supporting it. Middle-click never reaches here — the browser handles it on the Link.
            if (event.ctrlKey || event.metaKey || event.shiftKey) {
              window.open(itemPath(row), '_blank', 'noopener,noreferrer');
              return;
            }
            navigate(itemPath(row));
          }}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>

      {!isSearching && browse.data?.nextToken && (
        <Typography variant="caption" color="text.secondary">
          More than {PAGE_SIZE} items exist. Browsing shows the newest{' '}
          {PAGE_SIZE}; use search to reach the whole catalogue.
        </Typography>
      )}
    </>
  );
};

export default Catalogue;
